import _ from 'lodash';
import React, { useState } from 'react';
import BorderedIcon from '../components/BorderedIcon';
import Card, { CardSeparator } from '../components/Card';
import CardHeader from '../components/CardHeader';
import ContentPreview from '../components/ContentPreview';
import ExternalBrandLogo from '../components/ExternalBrandLogo';
import HoverPreviewable from '../components/HoverPreviewable';
import MetaList, { MetaListItem } from '../components/MetaList';
import Timestamp from '../components/Timestamp';
import type {
  MentionContentSlack,
  MentionType,
} from '../graphql/generated/graphql';
import ExternalSearchPreview from '../search/ExternalSearchPreview';
import SlackSearchResultContent from '../search/SlackSearchResultContent';
import renderHighlight from '../utils/search/renderHighlight';
import truncate from '../utils/text/truncate';
import getPlatformInfoFromMentionType from './getPlatformInfoFromMentionType';
import styles from './IntegrationMentionCard.module.scss';
import type { ExternalSearchPlatformInfo } from './IntegrationPlatformInfo';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';

const MENTION_FRAGMENT = gql(`
  fragment IntegrationMentionCard_mention on Mention {
    icon
    id
    status
    statusDate
    title
    type
    assignee {
      name
    }
    content {
      ... on MentionContentSlack {
        ...SlackSearchResultContent_content
      }
      ... on MentionContentDelta {
        delta
      }
      ... on MentionContentHighlights {
        highlights
      }
    }
    participants {
      name
    }
    preview {
      ...ExternalSearchPreview_preview
    }
    tags {
      id
      color
      name
    }
  }
`);

interface ImageWithFallbackProps {
  src: string;
  alt: string;
  fallback: string;
}

const ImageWithFallback = ({ src, fallback, alt }: ImageWithFallbackProps) => {
  const [finalSrc, setFinalSrc] = useState(src);
  return <img src={finalSrc} onError={() => setFinalSrc(fallback)} alt={alt} />;
};

interface PreviewIconProps {
  icon?: string | null;
  type: MentionType;
  integrationInfo: ExternalSearchPlatformInfo;
}

const PreviewIcon = ({ icon, type, integrationInfo }: PreviewIconProps) => {
  if (icon) {
    if (integrationInfo.platform === 'google-drive') {
      const scaledIcon = icon.replace(
        '//drive-thirdparty.googleusercontent.com/16',
        '//drive-thirdparty.googleusercontent.com/64',
      );
      return <ImageWithFallback fallback={icon} src={scaledIcon} alt={type} />;
    }

    return <img src={icon} alt={type} />;
  }

  return <ExternalBrandLogo {...integrationInfo.icon} />;
};

interface IntegrationMentionCardProps {
  activePreviewId?: string;
  asPopOver?: boolean;
  highlight?: boolean;
  url?: string;
  onActivePreviewIdChange?: (id: string) => unknown;
  onClick?: () => unknown;
  mention: DocumentType<typeof MENTION_FRAGMENT>;
}

const IntegrationMentionCard = ({
  activePreviewId,
  asPopOver,
  highlight,
  url,
  mention,
  onActivePreviewIdChange,
  onClick,
}: IntegrationMentionCardProps) => {
  const {
    id,
    title,
    assignee,
    participants,
    status,
    statusDate,
    tags,
    type,
    icon,
    preview,
    content,
  } = mention;
  const statusActor = participants?.[0]?.name;
  // TODO: remove `as` after getPlatformInfoFromMentionType is converted
  const integrationInfo = getPlatformInfoFromMentionType(
    type,
  ) as ExternalSearchPlatformInfo;
  const previewEnabled =
    integrationInfo.searchPreview && onActivePreviewIdChange;

  const previewIcon = (
    <PreviewIcon icon={icon} integrationInfo={integrationInfo} type={type} />
  );

  const iconElement =
    icon && integrationInfo.platform === 'slack' ? (
      <img
        className={styles.slackIcon}
        src={icon}
        alt={integrationInfo.platform}
      />
    ) : (
      <BorderedIcon>{previewIcon}</BorderedIcon>
    );

  let contentElement: React.ReactNode = null;

  if (content) {
    if (integrationInfo.platform === 'slack') {
      contentElement = (
        <SlackSearchResultContent content={content as MentionContentSlack} />
      );
    } else if ('highlights' in content && !!content.highlights?.length) {
      contentElement = (
        <div className={styles.content}>
          {content.highlights.map(
            (h) =>
              h && (
                <React.Fragment key={h}>{renderHighlight(h)}</React.Fragment>
              ),
          )}
        </div>
      );
    } else if ('delta' in content) {
      const delta =
        typeof content.delta === 'string'
          ? JSON.parse(content.delta)
          : content.delta;
      contentElement = (
        <ContentPreview content={delta} numLines={6} charsPerLine={50} />
      );
    }
  }

  const header = (targetProps?: React.HTMLProps<HTMLAnchorElement>) => (
    <CardHeader
      icon={
        <div className={styles.iconContainer}>
          {iconElement}
          {targetProps && (
            // eslint-disable-next-line jsx-a11y/anchor-has-content
            <a
              className={styles.hoverTarget}
              target="_blank"
              rel="noopener noreferrer"
              {...targetProps}
            />
          )}
        </div>
      }
      title={highlight ? renderHighlight(title) : truncate(title, 250)}
      meta={
        <MetaList>
          {status && (
            <MetaListItem>
              {_.capitalize(status)}{' '}
              {statusDate && <Timestamp date={statusDate} />}
              {statusActor && <> by {statusActor}</>}
            </MetaListItem>
          )}
          {assignee && <MetaListItem>Assigned to {assignee.name}</MetaListItem>}
        </MetaList>
      }
    />
  );

  return (
    <Card
      onClick={onClick}
      to={url}
      target="_blank"
      rel="noopener noreferrer"
      asPopOver={asPopOver}
    >
      {previewEnabled && url ? (
        <HoverPreviewable
          id={id}
          // eslint-disable-next-line react/no-unstable-nested-components
          preview={(_isOpen, updatePopOver) => (
            <ExternalSearchPreview
              externalId={id}
              platform={integrationInfo.platform}
              title={title}
              icon={previewIcon}
              url={url}
              preview={preview}
              updatePopOver={updatePopOver}
            />
          )}
          activeId={activePreviewId}
          onActiveIdChange={onActivePreviewIdChange}
        >
          {header}
        </HoverPreviewable>
      ) : (
        header()
      )}
      {!!contentElement && (
        <>
          <CardSeparator />
          {contentElement}
        </>
      )}

      {tags && tags.length > 0 && (
        <div className={styles.tags}>
          {tags.map(
            (tag) =>
              tag && (
                <div
                  className={styles.tag}
                  key={tag.id || tag.name}
                  style={tag.color ? { backgroundColor: tag.color } : undefined}
                >
                  {tag.name}
                </div>
              ),
          )}
        </div>
      )}
    </Card>
  );
};

export default IntegrationMentionCard;
